import { useState, useEffect } from "react";
import { Link } from "wouter";
import { useAuth } from "@/context/auth";
import { useBranch } from "@/context/branch";
import { formatCurrency } from "@/lib/currency";
import { readJsonArrayOrEmpty } from "@/lib/http";
import { sortSalesNewestFirst } from "@/lib/sales-order";
import { DashboardMoneyToggle, useDashboardMoneyVisibility } from "./dashboard-money-visibility";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ShoppingCart, Package, TrendingUp, AlertTriangle, BarChart3,
  Users, Truck, Receipt, ArrowRight, RefreshCcw,
} from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function StatCard({ label, value, sub, icon: Icon, color }: {
  label: string; value: string; sub?: string;
  icon: React.ElementType; color: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-start justify-between mb-3">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="min-w-0 break-words text-xl font-bold sm:text-2xl">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function SupermarketDashboard() {
  const { user, store } = useAuth();
  const { activeBranchId, activeBranch } = useBranch();
  const branchQuery = activeBranchId ? `?branchId=${activeBranchId}` : "";
  const isOwner = user?.role === "admin" || user?.role === "superadmin";
  const isManager = user?.role === "manager" || isOwner;
  const { moneyVisible, toggleMoneyVisibility, maskMoney } = useDashboardMoneyVisibility();

  const [stats, setStats] = useState({ todayRevenue: 0, todayTx: 0, monthRevenue: 0, lowStock: 0, products: 0, todayExpenses: 0 });
  const [lowStockItems, setLowStockItems] = useState<any[]>([]);
  const [recentSales, setRecentSales] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const lagosDate = (value: Date | string) =>
          new Date(value).toLocaleDateString("en-CA", { timeZone: "Africa/Lagos" });
        const today = lagosDate(new Date());
        const currentMonth = today.slice(0, 7);
        const [salesRes, productsRes, expensesRes] = await Promise.allSettled([
          fetch(`${BASE}/api/sales${branchQuery}`, { credentials: "include", cache: "no-store" }),
          fetch(`${BASE}/api/products${branchQuery}`, { credentials: "include" }),
          fetch(`${BASE}/api/expenses${branchQuery}`, { credentials: "include" }),
        ]);
        const sales = salesRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(salesRes.value) : [];
        const products = productsRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(productsRes.value) : [];
        const expenses = expensesRes.status === "fulfilled" ? await readJsonArrayOrEmpty<any>(expensesRes.value) : [];
        const todaySales = sales.filter((s: any) => lagosDate(s.createdAt) === today);
        const monthSales = sales.filter((s: any) => lagosDate(s.createdAt).startsWith(currentMonth));
        const low = products.filter((p: any) => Number(p.stock) <= Number(p.reorderLevel ?? 5));
        const todayExp = expenses.filter((e: any) => (e.date || "").startsWith(today));
        const newestSales = sortSalesNewestFirst(sales);
        setStats({
          todayRevenue: todaySales.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0),
          todayTx: todaySales.length,
          monthRevenue: monthSales.reduce((s: number, x: any) => s + Number(x.totalAmount ?? x.total ?? 0), 0),
          lowStock: low.length,
          products: products.length,
          todayExpenses: todayExp.reduce((s: number, x: any) => s + Number(x.amount || 0), 0),
        });
        setLowStockItems(low.slice(0, 5));
        setRecentSales(newestSales.slice(0, 5));
      } finally { setLoading(false); }
    };
    load();
  }, [branchQuery]);

  return (
    <div className="p-4 space-y-6 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
            <span className="text-2xl">🛒</span>
            <h1 className="min-w-0 break-words text-2xl font-bold">{store?.name || "My Store"}</h1>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Supermarket</Badge>
            <Badge variant="outline" className="capitalize">{user?.role}</Badge>
          </div>
          <p className="break-words text-muted-foreground text-sm">Supermarket & Grocery Dashboard · {activeBranch?.name ?? "All Branches"} · {new Date().toLocaleDateString("en-NG", { weekday: "long", day: "numeric", month: "long" })}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <DashboardMoneyToggle visible={moneyVisible} onToggle={toggleMoneyVisibility} />
          <Link href="/purchases?add=1"><Button variant="outline" size="sm" className="gap-1.5"><Truck className="w-3.5 h-3.5" />Purchase Order</Button></Link>
          <Link href="/sales/new"><Button size="sm" className="gap-1.5"><ShoppingCart className="w-3.5 h-3.5" />New Sale</Button></Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Today's Revenue" value={maskMoney(formatCurrency(stats.todayRevenue))} icon={TrendingUp} color="bg-emerald-100 text-emerald-600" sub={`${stats.todayTx} transactions`} />
        <StatCard label="This Month" value={maskMoney(formatCurrency(stats.monthRevenue))} icon={BarChart3} color="bg-blue-100 text-blue-600" />
        <StatCard label="Active Products" value={String(stats.products)} icon={Package} color="bg-violet-100 text-violet-600" sub={`${stats.lowStock} low stock`} />
        <StatCard label="Today's Expenses" value={maskMoney(formatCurrency(stats.todayExpenses))} icon={Receipt} color="bg-orange-100 text-orange-600" />
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: "POS / New Sale", href: "/sales/new", icon: ShoppingCart, color: "bg-emerald-50 border-emerald-200 hover:bg-emerald-100" },
            { label: "Add Product", href: "/inventory?add=1", icon: Package, color: "bg-blue-50 border-blue-200 hover:bg-blue-100" },
            { label: "Purchase Order", href: "/purchases?add=1", icon: Truck, color: "bg-amber-50 border-amber-200 hover:bg-amber-100" },
            { label: "Expenses", href: "/expenses", icon: Receipt, color: "bg-purple-50 border-purple-200 hover:bg-purple-100" },
          ].filter((a: any) => !a.mgr || isManager).map(a => (
            <Link key={a.label} href={a.href}>
              <div className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors text-slate-800 ${a.color}`}>
                <a.icon className="w-5 h-5 text-slate-700" />
                <span className="text-sm font-semibold">{a.label}</span>
                <ArrowRight className="w-3.5 h-3.5 ml-auto text-slate-500" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Low Stock Alerts */}
        {stats.lowStock > 0 && isManager && (
          <div className="bg-card border border-border rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <h3 className="font-semibold">Low Stock Alerts</h3>
              <Badge className="bg-amber-100 text-amber-700 border-0 ml-auto">{stats.lowStock} items</Badge>
            </div>
            {lowStockItems.length === 0 ? (
              <p className="text-sm text-muted-foreground">All products sufficiently stocked.</p>
            ) : (
              <div className="space-y-2">
                {lowStockItems.map((p: any) => (
                  <div key={p.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                    <span className="text-sm font-medium">{p.name}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${p.stock === 0 ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                      {p.stock === 0 ? "Out of stock" : `${p.stock} left`}
                    </span>
                  </div>
                ))}
                <Link href="/inventory">
                  <Button variant="outline" size="sm" className="w-full mt-2">View All Inventory</Button>
                </Link>
              </div>
            )}
          </div>
        )}

        {/* Recent Sales */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Recent Sales</h3>
            <Link href="/sales"><span className="text-xs text-primary hover:underline cursor-pointer">View all →</span></Link>
          </div>
          {recentSales.length === 0 ? (
            <div className="text-center py-8">
              <ShoppingCart className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No sales yet today</p>
              <Link href="/sales/new"><Button size="sm" className="mt-3">Record First Sale</Button></Link>
            </div>
          ) : (
            <div className="space-y-2">
              {recentSales.map((s: any) => (
                <div key={s.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                  <div>
                    <div className="text-sm font-medium">{s.customerName ?? "Walk-in Customer"}</div>
                    <div className="text-xs text-muted-foreground">{s.receiptNumber} · {new Date(s.createdAt).toLocaleString("en-NG", { timeZone: "Africa/Lagos", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold">{maskMoney(formatCurrency(Number(s.totalAmount ?? s.total ?? 0)))}</div>
                    <div className="text-xs text-muted-foreground capitalize">{s.paymentMethod || "cash"}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Owner-only: RBAC gated financials shortcut */}
      {isOwner && (
        <div className="bg-muted/40 border border-border rounded-xl p-5">
          <h3 className="font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4 text-primary" />Owner Analytics</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: "Profit & Loss", href: "/accounting/profit-loss" },
              { label: "Cash Flow", href: "/accounting/cash-flow" },
              { label: "Balance Sheet", href: "/accounting/balance-sheet" },
              { label: "Expense Reports", href: "/accounting/expense-reports" },
            ].map(l => (
              <Link key={l.label} href={l.href}>
                <Button variant="outline" size="sm" className="w-full text-xs">{l.label}</Button>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
